import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { GitBranch, Layers, GraduationCap, Download, Check, Loader2, Plus, X, Trash2, Store } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const TYPE_ICON = { workflow_template: GitBranch, project_template: Layers, academy_path: GraduationCap };

function PublishDialog({ onClose, onPublished, t }) {
  const [workflows, setWorkflows] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "Flujos", source_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/workflows").then((r) => setWorkflows(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.source_id) return toast.error(t("os.marketplace.selectSource"));
    setSaving(true);
    try {
      await api.post("/marketplace/publish", { type: "workflow_template", ...form });
      toast.success(t("os.marketplace.publishedToast"));
      onPublished(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mkt-publish-dialog">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className="card-surface w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{t("os.marketplace.publishTitle")}</h3>
          <button onClick={onClose} data-testid="mkt-publish-close" className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {workflows.length === 0 ? (
          <p className="text-sm text-navy/50">{t("os.marketplace.noWorkflows")}</p>
        ) : (
          <div className="space-y-3">
            <input className="field" placeholder={t("os.marketplace.name")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mkt-pub-title" />
            <input className="field" placeholder={t("os.marketplace.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="mkt-pub-desc" />
            <select className="field" value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })} data-testid="mkt-pub-source">
              <option value="">{t("os.marketplace.selectSource")}</option>
              {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn btn-ghost" data-testid="mkt-pub-cancel">{t("os.marketplace.cancel")}</button>
          {workflows.length > 0 && (
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="mkt-pub-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.marketplace.publishBtn")}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function Marketplace() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canInstall = ["admin", "ceo"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [showPublish, setShowPublish] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/marketplace"); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const install = async (id) => {
    setBusy(id);
    try { await api.post(`/marketplace/${id}/install`); toast.success(t("os.marketplace.installedToast")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/marketplace/${id}`); toast.success(t("os.marketplace.removed")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const tabs = [
    { key: "all", label: t("os.marketplace.all") },
    { key: "workflow_template", label: t("os.marketplace.workflow") },
    { key: "project_template", label: t("os.marketplace.project") },
    { key: "academy_path", label: t("os.marketplace.academy") },
  ];
  const shown = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <OSLayout title="Marketplace">
      <div className="space-y-8" data-testid="marketplace-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Store className="w-3.5 h-3.5" /> {t("os.marketplace.overline")}</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.marketplace.title")}</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">{t("os.marketplace.subtitle")}</p>
          </motion.div>
          {isAdmin && (
            <button onClick={() => setShowPublish(true)} className="btn btn-primary" data-testid="mkt-publish-btn">
              <Plus className="w-4 h-4" /> {t("os.marketplace.publish")}
            </button>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setFilter(tb.key)} data-testid={`mkt-tab-${tb.key}`}
              className={`btn text-sm ${filter === tb.key ? "btn-primary" : "btn-ghost"}`}>{tb.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : shown.length === 0 ? (
          <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="mkt-empty">{t("os.marketplace.empty")}</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mkt-grid">
            {shown.map((it) => {
              const Icon = TYPE_ICON[it.type] || Store;
              return (
                <div key={it.id} className="card-surface p-5 flex flex-col" data-testid={`mkt-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-10 h-10 rounded-lg bg-violet/10 flex items-center justify-center text-violet"><Icon className="w-5 h-5" /></div>
                    <span className="badge badge-muted">{it.category}</span>
                  </div>
                  <h3 className="font-medium mt-3">{it.title}</h3>
                  <p className="text-sm text-navy/50 mt-1 flex-1">{it.description}</p>
                  {it.scope === "tenant" && <div className="text-[11px] text-violet mt-2">{t("os.marketplace.byYou")}</div>}
                  <div className="mt-4 flex items-center gap-2">
                    {it.installed ? (
                      <span className="btn btn-outline text-sm pointer-events-none" data-testid={`mkt-installed-${it.id}`}><Check className="w-4 h-4" /> {t("os.marketplace.installed")}</span>
                    ) : canInstall ? (
                      <button onClick={() => install(it.id)} disabled={busy === it.id} className="btn btn-primary text-sm" data-testid={`mkt-install-${it.id}`}>
                        {busy === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("os.marketplace.install")}
                      </button>
                    ) : null}
                    {isAdmin && it.scope === "tenant" && (
                      <button onClick={() => remove(it.id)} className="text-navy/30 hover:text-destructive ml-auto" data-testid={`mkt-remove-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPublish && <PublishDialog onClose={() => setShowPublish(false)} onPublished={load} t={t} />}
    </OSLayout>
  );
}
